import { useFeatures } from "@/api/app-configuration.api";
import { type FilterCategory } from "@/data/filters/config";
import { essentialsText } from "@/hooks/use-trial-period";
import { useScreenSize } from "@/providers/ScreenSizeProvider";
import { Link, ToOptions } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { TrialCTAModal } from "../trial/TrialCTAModal";

type Props = {
  category: FilterCategory;
  link: ToOptions;
};

export function FilterCategoryCard({ category, link }: Props) {
  const { isHoverSupported } = useScreenSize();
  const { canUseContacts, canUseAdvancedFilters } = useFeatures();

  const [showTrialCTA, setShowTrialCTA] = useState(false);

  const showProBadge = useMemo(() => {
    return category.id === "contacts"
      ? !canUseContacts
      : !canUseAdvancedFilters && category.isAdvancedFilter;
  }, [canUseAdvancedFilters, category.isAdvancedFilter]);

  return (
    <>
      <li className="group relative">
        <Link
          {...link}
          className="outline-theme-primary flex h-48 flex-col justify-end rounded-sm bg-size-[100vw_100vh] bg-fixed p-6 outline-offset-2 transition-all duration-500 ease-in-out group-hover:bg-size-[calc(100vw+100px)_calc(100vh+100px)] focus-visible:outline-1"
          style={{
            backgroundImage:
              "linear-gradient(0deg, var(--theme-background-overlay) 0%, var(--theme-background-overlay) 100%), url('/bg.jpg')",
            backgroundBlendMode: "hard-light, normal",
          }}
          onClick={(event) => {
            if (showProBadge) {
              event.preventDefault();
              event.stopPropagation();
              setShowTrialCTA(true);
            }
          }}
        >
          {showProBadge && (
            <span className="text-theme-on-primary bg-theme-primary absolute top-4 right-4 rounded-full px-2 py-1 text-[0.625rem]/2 font-bold">
              Pro
            </span>
          )}
          <div className="absolute inset-0 w-full bg-linear-to-t from-black/25" />
          <div className="bg-theme-primary absolute top-6 left-6 z-1 w-fit rounded-full p-3 opacity-0 shadow-lg transition-opacity delay-50 duration-500 ease-in-out group-hover:opacity-100">
            <ArrowRight className="size-5 text-white" />
          </div>
          <span className="z-10 mb-1 truncate text-base leading-relaxed font-medium text-white xl:mb-0">
            {category.title}
          </span>
          <div
            className={twMerge(
              "transition-grid-template-rows z-10 grid duration-500 ease-in-out",
              isHoverSupported
                ? "grid-rows-[0fr] group-hover:grid-rows-[1fr]"
                : "grid-rows-[1fr]",
            )}
          >
            <div className="overflow-hidden">
              <p
                className={twMerge(
                  "text-[11px] font-medium text-white transition-opacity duration-200 group-hover:delay-150 group-hover:duration-700",
                  isHoverSupported
                    ? "opacity-0 group-hover:opacity-100"
                    : "opacity-100",
                )}
              >
                {category.description}
              </p>
            </div>
          </div>
        </Link>
      </li>
      <TrialCTAModal
        isOpen={showTrialCTA}
        title={essentialsText}
        onOpenChange={(open) => setShowTrialCTA(open)}
      />
    </>
  );
}
